import { useContext, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext, API } from "@/App";
import axios from "axios";
import { LogOut, Home, BookOpen, MessageCircle, BookMarked, TrendingUp, Dumbbell, Menu, X, User, Flame } from "lucide-react";
import CosmicLogo from "./CosmicLogo";

const Navigation = () => {
  const { user, logout, language } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [streak, setStreak] = useState(0);

  const t = (es, ca) => language === "ca" ? ca : es;

  useEffect(() => {
    if (user) fetchStreak();
  }, [user, location.pathname]);

  const fetchStreak = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/activity/weekly`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStreak(res.data.streak || 0);
    } catch (e) {
      // Ignore
    }
  };

  const navItems = [
    { path: "/dashboard", icon: Home, label: t("Inicio", "Inici"), testId: "nav-dashboard" },
    { path: "/lessons", icon: BookOpen, label: t("Lecciones", "Lliçons"), testId: "nav-lessons" },
    { path: "/exercises", icon: Dumbbell, label: t("Ejercicios", "Exercicis"), testId: "nav-exercises" },
    { path: "/chat", icon: MessageCircle, label: t("Chat IA", "Xat IA"), testId: "nav-chat" },
    { path: "/journal", icon: BookMarked, label: t("Diario", "Diari"), testId: "nav-journal" },
    { path: "/progress", icon: TrendingUp, label: t("Progreso", "Progrés"), testId: "nav-progress" },
  ];

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <nav className="bg-black/40 backdrop-blur-lg border-b border-gray-800 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo - Clickable to Dashboard */}
          <button
            data-testid="nav-logo"
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2.5 group hover:opacity-90 transition-all py-2 px-1 -ml-1 min-h-[44px] min-w-[44px]"
            aria-label="Anar a l'inici"
          >
            <div className="group-hover:scale-105 transition-transform">
              <CosmicLogo size={32} />
            </div>
            <span className="text-lg font-light tracking-wider bg-gradient-to-r from-teal-200 via-purple-100/40 to-slate-300 bg-clip-text text-transparent" style={{ filter: 'drop-shadow(0 0 6px rgba(127, 255, 212, 0.2))' }}>
              ESSENC·IA
            </span>
          </button>

          {/* Desktop Navigation - TOT PLATA */}
          <div className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  data-testid={item.testId}
                  onClick={() => navigate(item.path)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    isActive
                      ? "bg-slate-400/20 text-slate-300"
                      : "text-gray-400 hover:bg-white/5 hover:text-slate-200"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm">{item.label}</span>
                </button>
              );
            })}
          </div>

          {/* User Menu */}
          <div className="hidden md:flex items-center gap-4">
            {streak > 0 && (
              <div data-testid="nav-streak" className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-violet-500/15 border border-violet-400/25">
                <Flame className="w-4 h-4 text-violet-400" />
                <span className="text-sm font-semibold text-violet-300">{streak}</span>
              </div>
            )}
            <button
              data-testid="profile-link"
              onClick={() => navigate("/profile")}
              className="text-sm text-gray-400 hover:text-slate-300 transition-colors"
            >
              {user?.name}
            </button>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:text-red-400 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>{t("Salir", "Sortir")}</span>
            </button>
          </div>

          {/* Mobile: Streak + Menu Button */}
          <div className="md:hidden flex items-center gap-3">
            {streak > 0 && (
              <div data-testid="nav-streak-mobile" className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-500/15 border border-violet-400/25">
                <Flame className="w-3.5 h-3.5 text-violet-400" />
                <span className="text-sm font-semibold text-violet-300">{streak}</span>
              </div>
            )}
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-gray-300 hover:text-white"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-2" data-testid="mobile-menu">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  data-testid={`${item.testId}-mobile`}
                  onClick={() => {
                    navigate(item.path);
                    setMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive
                      ? "bg-slate-400/20 text-slate-300"
                      : "text-gray-300 hover:bg-white/5"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </button>
              );
            })}
            {/* AFEGIT: Perfil al mòbil */}
            <button
              data-testid="profile-link-mobile"
              onClick={() => {
                navigate("/profile");
                setMobileMenuOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-300 hover:bg-white/5 transition-all"
            >
              <User className="w-5 h-5" />
              <span>{t("Perfil", "Perfil")}</span>
            </button>
            <button
              data-testid="logout-btn-mobile"
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 text-gray-300 hover:text-red-400 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span>{t("Salir", "Sortir")}</span>
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
